import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { api } from '../lib/api';

export function LoginPage() {
  const location = useLocation();
  const routeState = location.state as { from?: string } | null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const { login } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const fromPath = routeState?.from && routeState.from !== '/login' ? routeState.from : '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error('Ange e-post och lösenord');
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      setShowResend(false);
      toast.success('Inloggad');
      navigate(fromPath, { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Inloggning misslyckades';
      if (msg === 'Email not verified') {
        setShowResend(true);
        toast.error('E-post är inte verifierad');
      } else {
        setShowResend(false);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const targetEmail = email.trim();
    if (!targetEmail) {
      toast.error('Ange e-post först');
      return;
    }
    setResendLoading(true);
    try {
      await api.post<{ ok: boolean }>('/auth/resend-verification', { email: targetEmail });
      toast.info('Verifieringsmail skickat (om kontot finns och inte redan är verifierat)');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Kunde inte skicka verifieringsmail');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <div className="w-full max-w-xs">
        <div className="h-10 flex items-center justify-center mb-6">
          <img src="/logga.png" alt="ApisAI" className="h-10 w-auto max-w-full" onError={(e) => { e.currentTarget.style.display = 'none'; (e.currentTarget.nextElementSibling as HTMLElement)?.classList.remove('hidden'); }} />
          <span className="hidden text-xl font-semibold text-primary">ApisAI</span>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-0.5">E-post</label>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-0.5">Lösenord</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full"
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-1.5 px-3 bg-primary text-white rounded hover:bg-primary-hover disabled:opacity-50"
          >
            {loading ? 'Loggar in...' : 'Logga in'}
          </button>
        </form>
        {showResend && (
          <div className="mt-3 border border-yellow-300 bg-yellow-50 rounded p-2 text-sm text-gray-700">
            <p className="mb-2">E-postadressen är inte verifierad. Vill du att vi skickar verifieringsmail igen?</p>
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={resendLoading}
              className="px-2 py-1 rounded bg-primary text-white hover:bg-primary-hover disabled:opacity-50"
            >
              {resendLoading ? 'Skickar...' : 'Skicka verifieringsmail igen'}
            </button>
          </div>
        )}
        <p className="text-sm text-gray-600 mt-2">
          <Link to="/forgot-password" className="text-primary hover:underline">
            Glömt lösenord?
          </Link>
        </p>
      </div>
    </div>
  );
}
